/**
 * Consignment Hooks
 * React hooks for consignment data management
 * Follows the same pattern as use-rentals.ts (useState + useEffect)
 */

import {
  getConsignmentsForRental,
  getConsignmentSummary,
  settleConsignment,
  type Consignment,
  type ConsignmentSummary,
  type SettleConsignmentInput,
} from '@/api/consignments';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UseConsignmentsForRentalResult {
  consignments: Consignment[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseConsignmentSummaryResult {
  summary: ConsignmentSummary | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseSettleConsignmentResult {
  settle: (rentalId: string, data: SettleConsignmentInput) => Promise<Consignment[]>;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to fetch consignments for a specific rental
 */
export function useConsignmentsForRental(
  rentalId: string | undefined
): UseConsignmentsForRentalResult {
  const [consignments, setConsignments] = useState<Consignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConsignments = useCallback(async () => {
    if (!rentalId) {
      setConsignments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getConsignmentsForRental(rentalId);
      setConsignments(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch consignments';
      setError(message);
      setConsignments([]);
    } finally {
      setIsLoading(false);
    }
  }, [rentalId]);

  useEffect(() => {
    void fetchConsignments();
  }, [fetchConsignments]);

  return {
    consignments,
    isLoading,
    error,
    refetch: fetchConsignments,
  };
}

/**
 * Hook to settle consignment items
 */
export function useSettleConsignment(): UseSettleConsignmentResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const settle = useCallback(async (rentalId: string, data: SettleConsignmentInput) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await settleConsignment(rentalId, data);
      return response.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to settle consignment';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { settle, isLoading, error };
}

/**
 * Hook to fetch consignment summary
 */
export function useConsignmentSummary(): UseConsignmentSummaryResult {
  const [summary, setSummary] = useState<ConsignmentSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getConsignmentSummary();
      setSummary(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch consignment summary';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  return {
    summary,
    isLoading,
    error,
    refetch: fetchSummary,
  };
}
